import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useRef, useState } from 'react';
import { startVoiceCall, VoiceError } from 'src/lib/livyVoice';
export const LIVY_FAILED_LINE = "I couldn't check that just now. You can try again or ask me in the chat.";
const UNCLEAR = '(the caller did not say anything clear)';
// The app hangs up this long before the limit so the reaper is the backstop, not the normal end.
const LIMIT_MARGIN_S = 5;
const WAIT_STREAM_MS = 20000;
/**
 * A call joined to the chat: each delegation from GPT-Live becomes an ordinary Livy turn on this conversation, so the
 * transcript, History and cards work as they do for typing, and the answer is handed back for GPT-Live to say.
 */
export function useLivyVoice(_ref) {
  let config = _ref.config,
    getToken = _ref.getToken,
    surface = _ref.surface,
    conversationId = _ref.conversationId,
    resident = _ref.resident,
    send = _ref.send,
    streaming = _ref.streaming;
  const _useState = useState('idle'),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    muted = _useState4[0],
    setMuted = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    seconds = _useState6[0],
    setSeconds = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    error = _useState8[0],
    setError = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    endedBy = _useState0[0],
    setEndedBy = _useState0[1];
  const _useState1 = useState(0),
    _useState10 = _slicedToArray(_useState1, 2),
    maxSeconds = _useState10[0],
    setMaxSeconds = _useState10[1];
  const stateRef = useRef(state);
  stateRef.current = state;
  const callRef = useRef(null);
  // Bumped by every hang-up, so a call still connecting when the user hangs up, switches company or leaves is ended
  // the moment it arrives instead of being adopted under the new chat.
  const generation = useRef(0);
  const heard = useRef('');
  // The latest send and streaming flag: a delegation arrives long after the render that started the call.
  const sendRef = useRef(send);
  sendRef.current = send;
  const streamingRef = useRef(streaming);
  streamingRef.current = streaming;

  /** The one hang-up path: drops the call (or the one still connecting) and closes it. */
  const drop = useCallback(() => {
    generation.current += 1;
    const call = callRef.current;
    callRef.current = null;
    return call ? call.end() : Promise.resolve();
  }, []);

  // Stable on purpose: LivyPage hangs up whenever end's identity changes (conversation and company effects).
  const end = useCallback(async function () {
    let by = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'hung_up';
    if (!callRef.current && stateRef.current !== 'connecting') return;
    setEndedBy(by);
    setState('ended');
    await drop();
  }, [drop]);
  const answer = useCallback(async (call, delegationId) => {
    setState('checking');
    call.note(delegationId, 'Checking the account now.');
    const question = heard.current.trim() || UNCLEAR;
    heard.current = '';
    // A typed turn still streaming would make send drop this one.
    const started = Date.now();
    while (streamingRef.current && Date.now() - started < WAIT_STREAM_MS) {
      await new Promise(r => setTimeout(r, 250));
    }
    // Hung up, or moved to another chat or company, while waiting: this question belongs to neither.
    if (callRef.current !== call) return;
    const text = await sendRef.current(question, undefined, {
      voice: true
    });
    if (callRef.current === call) call.say(delegationId, text || LIVY_FAILED_LINE);
  }, []);
  const start = useCallback(async audio => {
    if (!config || callRef.current || state === 'connecting') return;
    setError(null);
    setEndedBy(null);
    setSeconds(0);
    setMuted(false);
    setState('connecting');
    heard.current = '';
    const mine = generation.current;
    try {
      const token = await getToken();
      if (!token) throw new VoiceError('connect');
      let call = null;
      const onEvent = e => {
        if (!call || callRef.current !== call) return;
        if (e.type === 'started') setState('listening');else if (e.type === 'heard') {
          heard.current += e.delta;
          setState(s => s === 'speaking' ? 'listening' : s);
        } else if (e.type === 'said') setState('speaking');else if (e.type === 'delegation') void answer(call, e.id);else if (e.type === 'closed') void end('remote');else if (e.type === 'error') {
          // An error ends the call: the mic and the billed session must not stay up behind a hidden bar.
          setError(e.code);
          setState('error');
          void drop();
        }
      };
      call = await startVoiceCall({
        config,
        token,
        surface,
        conversationId,
        resident,
        audio,
        onEvent
      });
      if (generation.current !== mine) {
        void call.end();
        return;
      }
      callRef.current = call;
      setMaxSeconds(call.maxSeconds);
      setState(s => s === 'connecting' ? 'listening' : s);
    } catch (e) {
      if (generation.current !== mine) return;
      setError(e instanceof VoiceError ? e.code : 'unknown');
      setState('error');
    }
  }, [config, getToken, surface, conversationId, resident, answer, end, drop, state]);

  // The clock runs while a call is up, and hangs up before the limit.
  const live = state === 'listening' || state === 'checking' || state === 'speaking';
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, [live]);
  useEffect(() => {
    if (live && maxSeconds && seconds >= maxSeconds - LIMIT_MARGIN_S) void end('limit');
  }, [live, seconds, maxSeconds, end]);

  // Leaving the page hangs up.
  useEffect(() => () => void drop(), [drop]);
  const mute = useCallback(on => {
    var _callRef$current;
    (_callRef$current = callRef.current) === null || _callRef$current === void 0 || _callRef$current.mute(on);
    setMuted(on);
  }, []);
  return {
    state,
    muted,
    seconds,
    maxSeconds,
    error,
    endedBy,
    start,
    mute,
    end,
    active: live || state === 'connecting'
  };
}